/**
 * Simplified CIECAM02 color appearance model for substrate-specific color simulation
 * Focuses on lightness, chroma, and hue correlates with substrate-aware viewing conditions
 */

import Color from "colorjs.io";
import type { SubstrateProfile } from "../../types/substrate";

export interface ViewingConditions {
  illuminant: "D50" | "D65" | "A" | "F11";
  adaptingLuminance: number; // L_A in cd/m²
  backgroundLuminance: number; // Y_b
  surround: "average" | "dim" | "dark";
  discountIlluminant: boolean;
}

export interface ColorAppearanceCorrelates {
  J: number; // Lightness (0-100)
  C: number; // Chroma (0-100+)
  h: number; // Hue angle (0-360)
  Q: number; // Brightness
  M: number; // Colorfulness
  s: number; // Saturation
}

export interface SubstrateAppearanceResult {
  original: ColorAppearanceCorrelates;
  substrateAdjusted: ColorAppearanceCorrelates;
  appearanceDifference: {
    deltaJ: number; // Lightness difference
    deltaC: number; // Chroma difference
    deltaH: number; // Hue difference
    overallChange: number;
  };
  viewingConditions: ViewingConditions;
}

/**
 * Get default viewing conditions for print simulation
 */
export function getDefaultViewingConditions(
  illuminant: "D50" | "D65" = "D50",
): ViewingConditions {
  return {
    illuminant,
    adaptingLuminance: 64, // Typical office lighting (cd/m²)
    backgroundLuminance: 20, // Neutral gray background
    surround: "average",
    discountIlluminant: false,
  };
}

/**
 * Adjust viewing conditions based on substrate properties
 */
export function adjustViewingConditionsForSubstrate(
  baseConditions: ViewingConditions,
  substrate: SubstrateProfile,
): ViewingConditions {
  // Adjust adapting luminance based on substrate brightness
  const brightnessFactor = substrate.brightness / 100;
  const adjustedLuminance = baseConditions.adaptingLuminance * brightnessFactor;

  // Adjust surround based on substrate glossiness
  let surround: ViewingConditions["surround"] = baseConditions.surround;
  if (substrate.glossiness > 80) {
    surround = "average"; // Glossy surfaces appear in average surround
  } else if (substrate.glossiness > 40) {
    surround = "dim"; // Semi-gloss surfaces
  } else {
    surround = "dark"; // Matte surfaces
  }

  // Adjust background luminance based on substrate base color
  const baseColorLuminance =
    (substrate.baseColor.rgb.r * 0.2126 +
      substrate.baseColor.rgb.g * 0.7152 +
      substrate.baseColor.rgb.b * 0.0722) /
    255;
  const adjustedBackground =
    baseConditions.backgroundLuminance * (0.5 + baseColorLuminance * 0.5);

  return {
    ...baseConditions,
    adaptingLuminance: adjustedLuminance,
    backgroundLuminance: adjustedBackground,
    surround,
  };
}

/**
 * Simplified CIECAM02 forward transform
 * Calculates J (lightness), C (chroma), and h (hue) correlates
 */
export function calculateCIECAM02(
  color: string,
  viewingConditions: ViewingConditions,
): ColorAppearanceCorrelates {
  try {
    const colorObj = new Color(color);
    const xyz = colorObj.to("xyz-d65");
    const [X, Y, Z] = xyz.coords;

    // Validate inputs
    if (!X || !Y || !Z || !isFinite(X) || !isFinite(Y) || !isFinite(Z)) {
      throw new Error("Invalid XYZ color values");
    }

    // Simplified CIECAM02 parameters
    const {
      adaptingLuminance: L_A,
      backgroundLuminance: Y_b,
      surround,
    } = viewingConditions;

    // Validate viewing conditions
    if (L_A <= 0 || Y_b <= 0) {
      throw new Error("Invalid luminance values in viewing conditions");
    }

    // Surround factors (simplified)
    const F = 1.0; // Factor for complete adaptation
    const c = surround === "average" ? 0.69 : surround === "dim" ? 0.59 : 0.525;
    const N_c = surround === "average" ? 1.0 : surround === "dim" ? 0.9 : 0.8;

    // Calculate adaptation factors with error checking
    const k = 1 / (5 * L_A + 1);
    const k4 = k ** 4;
    const k_pow_third = k ** (1 / 3);

    if (!isFinite(k4) || !isFinite(k_pow_third)) {
      throw new Error("Invalid adaptation factor calculation");
    }

    const F_L = 0.2 * k4 * (5 * L_A) + 0.1 * (1 - k4) * (5 * L_A) ** (1 / 3);
    const n = Y_b / 100;

    if (!isFinite(F_L) || n <= 0) {
      throw new Error("Invalid luminance adaptation calculation");
    }

    // Calculate z and N with error checking
    const sqrt_n = Math.sqrt(n);
    if (!isFinite(sqrt_n)) {
      throw new Error("Invalid square root calculation");
    }

    const z = 1.48 + sqrt_n;
    const N = 0.725 * Math.pow(n, -0.2);
    const N_bb = N;
    const N_cb = N_bb;

    // White point (D65)
    const X_w = 95.047;
    const Y_w = 100.0;
    const Z_w = 108.883;

    // Chromatic adaptation transform (simplified Bradford)
    const R_a = X;
    const G_a = Y;
    const B_a = Z;

    const R_aw = X_w;
    const G_aw = Y_w;
    const B_aw = Z_w;

    // Calculate D (degree of adaptation) with bounds checking
    const exponent = (-L_A - 42) / 92;
    const D = F * (1 - (1 / 3.6) * Math.exp(exponent));

    if (!isFinite(D) || D < 0 || D > 1) {
      console.warn("Degree of adaptation out of bounds, using fallback value");
    }

    // Adapted cone responses with error checking
    const D_safe = Math.max(0, Math.min(1, D));
    const R_c = (Y_b / R_aw) * D_safe * R_a + (1 - D_safe) * R_a;
    const G_c = (Y_b / G_aw) * D_safe * G_a + (1 - D_safe) * G_a;
    const B_c = (Y_b / B_aw) * D_safe * B_a + (1 - D_safe) * B_a;

    // Validate adapted responses
    if (!isFinite(R_c) || !isFinite(G_c) || !isFinite(B_c)) {
      throw new Error("Invalid adapted cone responses");
    }

    // Post-adaptation non-linear response compression with error checking
    const safeCompress = (value: number): number => {
      if (value === 0) return 0;
      const absValue = Math.abs(value);
      const normalized = absValue / 100;
      const powered = Math.pow(normalized, 0.42);
      const denominator = 27.13 + powered;

      if (denominator === 0 || !isFinite(powered) || !isFinite(denominator)) {
        console.warn("Non-linear compression failed, using fallback");
        return Math.sign(value) * Math.min(100, absValue);
      }

      const result = (Math.sign(value) * 400 * powered) / denominator;
      return isFinite(result)
        ? result
        : Math.sign(value) * Math.min(100, absValue);
    };

    const R_a_prime = safeCompress(R_c);
    const G_a_prime = safeCompress(G_c);
    const B_a_prime = safeCompress(B_c);

    // Calculate opponent dimensions
    const a = R_a_prime * 11 + G_a_prime * -12 + B_a_prime * 1;
    const b =
      R_a_prime * (1 / 11) + G_a_prime * (1 / 11) + B_a_prime * (-2 / 11);

    // Calculate hue angle with error checking
    const h_rad = Math.atan2(b, a);
    if (!isFinite(h_rad)) {
      console.warn("Invalid hue angle calculation, using fallback");
    }
    const h = ((h_rad * 180) / Math.PI + 360) % 360;

    // Calculate eccentricity factor
    const cos_term = Math.cos(h_rad * 2 + 2);
    const e_t = (1 / 4) * (cos_term + 3.8);

    // Calculate achromatic response with error checking
    const A = (2 * R_a_prime + G_a_prime + (1 / 20) * B_a_prime - 0.305) * N_bb;

    if (!isFinite(A) || A < 0) {
      console.warn("Invalid achromatic response, using fallback");
    }

    // Calculate lightness (J) with bounds checking
    const A_safe = Math.max(0, A);
    const J = 100 * Math.pow(A_safe / 100, c * z);

    if (!isFinite(J) || J < 0 || J > 100) {
      console.warn("Lightness out of bounds, clamping to valid range");
    }

    // Calculate chroma (C) with error checking
    const denominator_C = R_a_prime + G_a_prime + (21 / 20) * B_a_prime;
    const t =
      denominator_C !== 0
        ? (e_t * Math.sqrt(a * a + b * b)) / denominator_C
        : 0;

    const J_safe = Math.max(0, Math.min(100, J));
    const t_pow = Math.pow(t, 0.9);
    const J_pow = Math.pow(J_safe / 100, 0.5);
    const n_pow = Math.pow(0.29, n);
    const chroma_factor = Math.pow(1.64 - n_pow, 0.73);

    const C = t_pow * J_pow * chroma_factor;

    if (!isFinite(C) || C < 0) {
      console.warn("Invalid chroma calculation, using fallback");
    }

    // Calculate brightness (Q) with error checking
    const Q_base = Math.sqrt(J_safe / 100);
    const Q = (4 / c) * Q_base * (A_safe + 4) * Math.pow(F_L, 0.25);

    if (!isFinite(Q) || Q < 0) {
      console.warn("Invalid brightness calculation, using fallback");
    }

    // Calculate colorfulness (M) with error checking
    const C_safe = Math.max(0, C);
    const M = C_safe * Math.pow(F_L, 0.25);

    if (!isFinite(M) || M < 0) {
      console.warn("Invalid colorfulness calculation, using fallback");
    }

    // Calculate saturation (s) with error checking
    const Q_safe = Math.max(0, Q);
    const s = Q_safe > 0 ? 100 * Math.sqrt(M / Q_safe) : 0;

    if (!isFinite(s) || s < 0 || s > 100) {
      console.warn("Invalid saturation calculation, clamping to valid range");
    }

    return {
      J: Math.max(0, Math.min(100, J)),
      C: Math.max(0, C),
      h: Math.max(0, Math.min(360, h)),
      Q: Math.max(0, Q),
      M: Math.max(0, M),
      s: Math.max(0, Math.min(100, s)),
    };
  } catch (error) {
    console.error("CIECAM02 calculation failed:", error);
    // Fallback values if calculation fails
    return {
      J: 50,
      C: 0,
      h: 0,
      Q: 50,
      M: 0,
      s: 0,
    };
  }
}

/**
 * Analyze color appearance changes due to substrate
 */
export function analyzeSubstrateAppearance(
  color: string,
  substrate: SubstrateProfile,
  baseViewingConditions?: ViewingConditions,
): SubstrateAppearanceResult {
  const baseConditions =
    baseViewingConditions || getDefaultViewingConditions("D50");
  const substrateConditions = adjustViewingConditionsForSubstrate(
    baseConditions,
    substrate,
  );

  // Calculate appearance correlates for both conditions
  const originalAppearance = calculateCIECAM02(color, baseConditions);
  const substrateAppearance = calculateCIECAM02(color, substrateConditions);

  // Calculate appearance differences
  const deltaJ = Math.abs(originalAppearance.J - substrateAppearance.J);
  const deltaC = Math.abs(originalAppearance.C - substrateAppearance.C);

  // Calculate hue difference (circular)
  let deltaH = Math.abs(originalAppearance.h - substrateAppearance.h);
  if (deltaH > 180) {
    deltaH = 360 - deltaH;
  }

  // Overall appearance change (weighted combination)
  const overallChange =
    Math.sqrt(
      Math.pow(deltaJ / 100, 2) +
        Math.pow(deltaC / 100, 2) +
        Math.pow(deltaH / 180, 2),
    ) * 100;

  return {
    original: originalAppearance,
    substrateAdjusted: substrateAppearance,
    appearanceDifference: {
      deltaJ,
      deltaC,
      deltaH,
      overallChange,
    },
    viewingConditions: substrateConditions,
  };
}

/**
 * Predict color appearance on substrate
 */
export function predictSubstrateColorAppearance(
  color: string,
  substrate: SubstrateProfile,
  options: {
    viewingConditions?: ViewingConditions;
    includeAdaptation?: boolean;
  } = {},
): ColorAppearanceCorrelates {
  const baseConditions =
    options.viewingConditions || getDefaultViewingConditions("D50");
  const finalConditions =
    options.includeAdaptation !== false
      ? adjustViewingConditionsForSubstrate(baseConditions, substrate)
      : baseConditions;

  return calculateCIECAM02(color, finalConditions);
}

/**
 * Compare appearance between two substrates
 */
export function compareSubstrateAppearance(
  color: string,
  substrate1: SubstrateProfile,
  substrate2: SubstrateProfile,
  baseViewingConditions?: ViewingConditions,
): {
  substrate1: ColorAppearanceCorrelates;
  substrate2: ColorAppearanceCorrelates;
  appearanceDifference: {
    deltaJ: number;
    deltaC: number;
    deltaH: number;
    overallDifference: number;
  };
  recommendation: string;
} {
  const appearance1 = analyzeSubstrateAppearance(
    color,
    substrate1,
    baseViewingConditions,
  );
  const appearance2 = analyzeSubstrateAppearance(
    color,
    substrate2,
    baseViewingConditions,
  );

  const deltaJ = Math.abs(
    appearance1.substrateAdjusted.J - appearance2.substrateAdjusted.J,
  );
  const deltaC = Math.abs(
    appearance1.substrateAdjusted.C - appearance2.substrateAdjusted.C,
  );

  let deltaH = Math.abs(
    appearance1.substrateAdjusted.h - appearance2.substrateAdjusted.h,
  );
  if (deltaH > 180) {
    deltaH = 360 - deltaH;
  }

  const overallDifference =
    Math.sqrt(
      Math.pow(deltaJ / 100, 2) +
        Math.pow(deltaC / 100, 2) +
        Math.pow(deltaH / 180, 2),
    ) * 100;

  // Generate recommendation
  let recommendation = "";
  if (overallDifference < 5) {
    recommendation = "Both substrates will produce similar color appearance";
  } else if (overallDifference < 15) {
    recommendation =
      "Moderate appearance difference - consider viewing conditions";
  } else {
    recommendation =
      "Significant appearance difference - substrate choice is critical";
  }

  if (substrate1.brightness > substrate2.brightness && deltaJ > 10) {
    recommendation += `; ${substrate1.name} will appear brighter`;
  } else if (substrate2.brightness > substrate1.brightness && deltaJ > 10) {
    recommendation += `; ${substrate2.name} will appear brighter`;
  }

  if (substrate1.glossiness > substrate2.glossiness && deltaC > 10) {
    recommendation += `; ${substrate1.name} will show higher chroma`;
  } else if (substrate2.glossiness > substrate1.glossiness && deltaC > 10) {
    recommendation += `; ${substrate2.name} will show higher chroma`;
  }

  return {
    substrate1: appearance1.substrateAdjusted,
    substrate2: appearance2.substrateAdjusted,
    appearanceDifference: {
      deltaJ,
      deltaC,
      deltaH,
      overallDifference,
    },
    recommendation,
  };
}

/**
 * Generate appearance report for a color on multiple substrates
 */
export function generateAppearanceReport(
  color: string,
  substrates: SubstrateProfile[],
  baseViewingConditions?: ViewingConditions,
): {
  color: string;
  baseConditions: ViewingConditions;
  substrateResults: Array<{
    substrate: SubstrateProfile;
    appearance: ColorAppearanceCorrelates;
    appearanceChange: {
      deltaJ: number;
      deltaC: number;
      deltaH: number;
      overallChange: number;
    };
  }>;
  bestForBrightness: SubstrateProfile;
  bestForChroma: SubstrateProfile;
  mostNatural: SubstrateProfile;
} {
  // Validate input
  if (!substrates || substrates.length === 0) {
    throw new Error("At least one substrate must be provided");
  }

  const baseConditions =
    baseViewingConditions || getDefaultViewingConditions("D50");
  const originalAppearance = calculateCIECAM02(color, baseConditions);

  const substrateResults = substrates.map((substrate) => {
    const analysis = analyzeSubstrateAppearance(
      color,
      substrate,
      baseConditions,
    );
    return {
      substrate,
      appearance: analysis.substrateAdjusted,
      appearanceChange: analysis.appearanceDifference,
    };
  });

  // Find best substrates for different criteria with proper reduce initial value
  const bestForBrightness = substrateResults.reduce((best, current) =>
    current.appearance.J > best.appearance.J ? current : best,
  ).substrate;

  const bestForChroma = substrateResults.reduce((best, current) =>
    current.appearance.C > best.appearance.C ? current : best,
  ).substrate;

  const mostNatural = substrateResults.reduce((best, current) => {
    const currChange =
      (current.appearanceChange as any).overallAppearanceChange || 0;
    const bestChange =
      (best.appearanceChange as any).overallAppearanceChange || 0;
    return currChange < bestChange ? current : best;
  }).substrate;

  return {
    color,
    baseConditions,
    substrateResults: substrateResults as any,
    bestForBrightness,
    bestForChroma,
    mostNatural,
  };
}
